// @flow
import {ActionsObservable, ofType} from 'redux-observable';
import {
  saveAccountApi,
  saveSubscriptionSettingApi,
  fetchSubscriptionSettingApi,
  changePasswordApi,
} from './api';
import {map, switchMap} from 'rxjs/operators';
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {LoginSuccess, GetSessionSuccess} from '../../auth/duck';

import type {User, SubscriptionSetting} from '../../../entities';
import type {Action} from '../../../store/utils';
import {debounceTime} from 'rxjs/operators/index';
import {PerformSubscribeSuccess} from '../../checkout/duck';

export type AccountConfigAction = Action & {
  error?: string,
  user?: User,
  accountConfigRequest?: {
    automaticCharge?: boolean,
    expiredSubscription?: boolean,
    storeTierPlanId?: string,
    currency?: string,
  },
};

export type AccountConfigState = {
  isSaving?: boolean,
  isFetching?: boolean,
  error?: string,
  savingSuccess?: boolean,
  fetchingSuccess?: boolean,
  subscriptionSetting?: SubscriptionSetting,
  isFetchingSubscriptionSetting?: boolean,
  saveSubscriptionSettingError?: boolean,
};

export class SaveAccountSettings extends ActionCreator<AccountConfigAction> {}
export class SaveAccountSettingsSuccess extends ActionCreator<AccountConfigAction> {}
export class SaveAccountSettingsErrorFn extends ActionCreator<AccountConfigAction> {}
export class SaveAccountResetState extends ActionCreator<AccountConfigAction> {}

export class FetchSubscriptionSettingSuccess extends ActionCreator<AccountConfigAction> {}
export class FetchSubscriptionSettingErrorFn extends ActionCreator<AccountConfigAction> {}

export class SaveSubscriptionSetting extends ActionCreator<AccountConfigAction> {}
export class SaveSubscriptionSettingSuccess extends ActionCreator<AccountConfigAction> {}
export class SaveSubscriptionSettingErrorFn extends ActionCreator<AccountConfigAction> {}

export class SaveAutomatiCharge extends ActionCreator<AccountConfigAction> {}
export class SaveAutomatiChargeSuccess extends ActionCreator<AccountConfigAction> {}
export class SaveAutomatiChargeErrorFn extends ActionCreator<AccountConfigAction> {}

export class ChangePassword extends ActionCreator<AccountConfigAction> {}
export class ChangePasswordSuccess extends ActionCreator<AccountConfigAction> {}
export class ChangePasswordErrorFn extends ActionCreator<AccountConfigAction> {}

export const actions = {
  saveAccountSettings: (payload: AccountConfigAction) =>
    new SaveAccountSettings(payload),
  resetState: () => new SaveAccountResetState(),
  saveSubscriptionSetting: (payload: AccountConfigAction) =>
    new SaveSubscriptionSetting(payload),

  changeAccountPassword: (password: string) =>
    new ChangePassword({data: {password}}),
};

export const initialState = {};

export default createReducer(initialState, {
  [SaveAccountSettings.type]: (state: AccountConfigState) => ({
    ...state,
    isSaving: true,
    savingSuccess: false,
  }),
  [SaveAccountSettingsSuccess.type]: (state: AccountConfigState) => ({
    ...state,
    isSaving: false,
    savingSuccess: true,
  }),
  [SaveAccountSettingsErrorFn.type]: (
    state: AccountConfigState,
    action: AccountConfigAction
  ) => ({
    ...state,
    error: action.error,
    isSaving: false,
    savingSuccess: false,
  }),
  [PerformSubscribeSuccess.type]: (state: AccountConfigState) => ({
    ...state,
    isFetchingSubscriptionSetting: true,
  }),
  [FetchSubscriptionSettingSuccess.type]: (
    state: AccountConfigState,
    action: AccountConfigAction
  ) => ({
    ...state,
    subscriptionSetting: action.data,
    fetchingSuccess: true,
    isFetchingSubscriptionSetting: undefined,
  }),
  [FetchSubscriptionSettingErrorFn.type]: (state: AccountConfigState) => ({
    ...state,
    subscriptionSetting: null,
  }),
  [SaveSubscriptionSetting.type]: (
    state: AccountConfigState,
    action: AccountConfigAction
  ) => {
    const result = {
      ...state,
      isSavingSubscriptionSetting: true,
      savingSuccess: false,
      saveSubscriptionSettingError: undefined,
    };
    if (
      action.accountConfigRequest &&
      typeof action.accountConfigRequest.automaticCharge === 'boolean'
    )
      result.subscriptionSetting = {
        ...state.subscriptionSetting,
        automaticCharge: action.accountConfigRequest.automaticCharge,
      };
    return result;
  },
  [SaveSubscriptionSettingSuccess.type]: (
    state: AccountConfigState,
    action: AccountConfigAction
  ) => ({
    ...state,
    subscriptionSetting: action.data,
    savingSuccess: action.reset ? undefined : true,
    isSavingSubscriptionSetting: false,
    saveSubscriptionSettingError: undefined,
  }),
  [SaveSubscriptionSettingErrorFn.type]: (
    state: AccountConfigState,
    action: AccountConfigAction
  ) => ({
    ...state,
    error: action.error,
    isSavingSubscriptionSetting: false,
    savingSuccess: false,
    saveSubscriptionSettingError: action.error,
  }),
  [SaveAccountResetState.type]: state => ({
    ...initialState,
    subscriptionSetting: state.subscriptionSetting,
  }),

  [ChangePassword.type]: (state: AccountConfigState) => ({
    ...state,
    isSaving: true,
    savingSuccess: false,
  }),
  [ChangePasswordSuccess.type]: (state: AccountConfigState) => ({
    ...state,
    isSaving: false,
    savingSuccess: true,
  }),
  [ChangePasswordErrorFn.type]: (
    state: AccountConfigState,
    action: AccountConfigAction
  ) => ({
    ...state,
    error: action.error,
    isSaving: false,
    savingSuccess: false,
  }),
});

export function saveAccountEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveAccountSettings.type),
    switchMap(({data}: AccountConfigAction) =>
      saveAccountApi(data).pipe(
        map((user: User) => new SaveAccountSettingsSuccess({user})),
        buildCatchError(
          SaveAccountSettingsErrorFn,
          'No se pudo guardar los datos correctamente'
        )
      )
    )
  );
}

export function changeAccountPasswordEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(ChangePassword.type),
    switchMap(({data}: AccountConfigAction) =>
      changePasswordApi(data).pipe(
        map((user: User) => new ChangePasswordSuccess()),
        buildCatchError(
          ChangePasswordErrorFn,
          'No se pudo guardar los datos correctamente'
        )
      )
    )
  );
}

export function fetchSubscriptionSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      GetSessionSuccess.type,
      LoginSuccess.type,
      PerformSubscribeSuccess.type
    ),
    switchMap(() =>
      fetchSubscriptionSettingApi().pipe(
        map((subscriptionSetting: SubscriptionSetting) => {
          return new FetchSubscriptionSettingSuccess({
            data: subscriptionSetting,
          });
        }),
        buildCatchError(FetchSubscriptionSettingErrorFn)
      )
    )
  );
}

export function saveSubscriptionSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveSubscriptionSetting.type),
    debounceTime(250),
    switchMap(({accountConfigRequest, reset = false}: AccountConfigAction) =>
      saveSubscriptionSettingApi(accountConfigRequest).pipe(
        map(
          (subscriptionSetting: SubscriptionSetting) =>
            new SaveSubscriptionSettingSuccess({
              data: subscriptionSetting,
              reset,
            })
        ),
        buildCatchError(
          SaveSubscriptionSettingErrorFn,
          'No se guardo correctamente el cambio del cargo automático'
        )
      )
    )
  );
}
